import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import axiosPrivate from 'config/axiosPrivate';
import DetailsIATraduction from '../detailsIAtraduction/detailsia';
import { InitialTextSection } from 'components/shared/InitialTextSection';
import { ROLES } from 'utils/Constants';
import { removeStrongTags } from 'utils/StringUtils';
import { CustomEditor } from '../CustomEditor';
import ErrorSuccesModal from '../modal/ErrorSuccesModal';
import QuitProjectCreationModal from 'components/shared/modal/QuitProjectCreationModal';
import WordPressDeployModal from 'components/shared/modal/WordPressDeployModal';
import { isTextRevisionNotAllowedError } from 'utils/hooks/useTextStatusChange';
import ProjectCreatedModal from 'components/shared/modal/ProjectCreatedModal';
import BuyCreditsModal from 'components/shared/BuyCreditsModal';
import { DownloadOneTextWordType } from 'components/shared/DownloadFile/DownloadTextWordType';
import { UserSessionSelector } from 'store/UserSession';
import {
  saveTranslationSnapshot,
  clearTranslationSnapshot,
  translationFormSelector,
} from 'store/AdminClient/SmartTextForm';
import { adminClientClientListSelector } from 'store/AdminClient/ClientList';
import { fetchAdminClientClientList } from 'store/AdminClient/ClientList/features';
import usePageTracking from 'utils/hooks/usePageTracking';
import { NAVIGATING_TO_TRANSLATION_IA_KEY } from 'utils/smartContent/useSmartTextTranslation';

const TRANSLATION_POPIN_CONTEXT_KEY = 'TranslationSmartContentPopinContext';
const GENERATION_ERROR_MESSAGE = "Une erreur est survenue lors de la génération. Veuillez réessayer. Si le problème persiste, contactez notre support.";

const CreateTraductionIA = ({ mode, match }) => {
  const history = useHistory();
  const location = useLocation();
  const params = useParams();
  const idFromPathname = (() => {
    const matched = location.pathname.match(/\/CreateTraductionIA\/([^/?#]+)/i);
    return matched?.[1] ? decodeURIComponent(matched[1]) : null;
  })();
  const routeTextId = params?.id || match?.params?.id || idFromPathname || null;
  const isConsultantRoute =
    mode === 'consultant' || history.location.pathname.startsWith('/Consultant');
  const translationIaListPath = isConsultantRoute
    ? '/Consultant/TraductionIA'
    : '/AdminClient/TraductionIA';
  const createTranslationIaPath = isConsultantRoute
    ? '/Consultant/CreateTraductionIA'
    : '/AdminClient/CreateTraductionIA';
  usePageTracking(createTranslationIaPath);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const reduxTranslationState = useSelector(translationFormSelector);
  const { clients = [] } = useSelector(adminClientClientListSelector) || {};
  const { user } = useSelector(UserSessionSelector);
  const [expanded, setExpanded] = useState(true);
  const [formData, setFormData] = useState({
    keyword: '',
    url: '',
    titleTag: '',
    metaDescriptionTag: '',
    agentIA: '',
    websiteId: ''
  });
  const [errors, setErrors] = useState({});
  const [editorContent, setEditorContent] = useState("");
  const [translatedContent, setTranslatedContent] = useState("");
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationAgents, setTranslationAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [currentTextId, setCurrentTextId] = useState(routeTextId ? String(routeTextId) : null);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [isDelivering, setIsDelivering] = useState(false);
  const [deliverError, setDeliverError] = useState('');
  const [deliverSuccess, setDeliverSuccess] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validateError, setValidateError] = useState('');
  const [validateSuccess, setValidateSuccess] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  const [isGoingOnline, setIsGoingOnline] = useState(false);
  const [deployError, setDeployError] = useState('');
  const [deploySuccess, setDeploySuccess] = useState(false);
  const [showWordPressDeployModal, setShowWordPressDeployModal] = useState(false);
  const [wordPressDeployContent, setWordPressDeployContent] = useState('');
  const [isSendingToRevision, setIsSendingToRevision] = useState(false);
  const [revisionError, setRevisionError] = useState('');

  const [errorSuccess, setErrorSuccess] = useState(false);
  const [errorSuccessMsg, setErrorSuccessMsg] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [initialTextContent, setInitialTextContent] = useState('');
  const [initialTextWordCount, setInitialTextWordCount] = useState(0);
  const [selectedWebsiteId, setSelectedWebsiteId] = useState(null);
  const [collapseInitialEditorKey, setCollapseInitialEditorKey] = useState(0);
  const [remainingCredits, setRemainingCredits] = useState(null);
  const [isLimitModalOpen, setIsLimitModalOpen] = useState(false);
  const [isBuyCreditsModalOpen, setIsBuyCreditsModalOpen] = useState(false);
  const [showSaveBeforeDownloadModal, setShowSaveBeforeDownloadModal] = useState(false);
  const [isRevisionErrorModalOpen, setIsRevisionErrorModalOpen] = useState(false);

  const resolveActualTextId = useCallback((fallbackId = null) => {
    const fromUrl = routeTextId ? String(routeTextId) : null;
    const fromStorage = localStorage.getItem('AdminClient_TextId');
    return fromUrl || fromStorage || (fallbackId != null ? String(fallbackId) : null);
  }, [routeTextId]);

  const syncTextIdStorageAndUrl = useCallback((id) => {
    if (!id) return;
    const idStr = String(id);
    try {
      localStorage.setItem('AdminClient_TextId', idStr);
    } catch { }
    setCurrentTextId(idStr);
    const targetPath = `${createTranslationIaPath}/${idStr}`;
    if (location.pathname !== targetPath) {
      history.replace(`${targetPath}${location.search || ''}`);
    }
  }, [createTranslationIaPath, history, location.pathname, location.search]);

  useEffect(() => {
    if (!routeTextId) return;
    try {
      localStorage.setItem('AdminClient_TextId', String(routeTextId));
    } catch { }
    setCurrentTextId(String(routeTextId));
  }, [routeTextId]);

  const handleDownloadWord = useCallback(() => {
    if (!currentTextId) {
      setShowSaveBeforeDownloadModal(true);
      return;
    }
    DownloadOneTextWordType(currentTextId, user);
  }, [currentTextId, user]);

  const refreshCompanyQuotas = useCallback(async () => {
    const { data } = await axiosPrivate.get('/project/company-quotas');
    const totalCredits = Number(data?.totalCredits);
    const usedCredits = Number(data?.usedCredits);
    if (Number.isFinite(totalCredits) && Number.isFinite(usedCredits)) {
      setRemainingCredits(Math.max(0, totalCredits - usedCredits));
    } else {
      const apiRemainingCredits = Number(data?.remainingCredits);
      setRemainingCredits(Number.isFinite(apiRemainingCredits) ? apiRemainingCredits : null);
    }
  }, []);

  const refreshCreditsWithDelay = useCallback(
    async (delayMs = 0) => {
      if (delayMs > 0) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
      await refreshCompanyQuotas();
    },
    [refreshCompanyQuotas]
  );

  const handleChange = (field) => (e) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
    setErrors(prev => {
      const updated = { ...prev };
      if (field === 'keyword') updated.keywordError = false;
      if (field === 'agentIA') updated.agentError = false;
      if (field === 'titleTag') updated.titleError = false;
      if (field === 'metaDescriptionTag') updated.metaDescriptionError = false;
      return updated;
    });
  };

  const handleAgentChange = (agent) => {

    if (agent) {

      setFormData(prev => ({
        ...prev,
        agentIA: agent.id,
        websiteId: agent.websiteId || ''
      }));
      setErrors(prev => ({ ...prev, agentError: false }));
    } else {
      setFormData(prev => ({
        ...prev,
        agentIA: '',
        websiteId: ''
      }));
    }
  };

  const handleBlur = (field) => (e) => {
  };

  const isFieldDisabled = () => isConsultantRoute;

  const handleEditorChange = (content, editor) => {
    setEditorContent(content);
    if (content && content.trim() !== '') {
      setErrors(prev => ({ ...prev, editorError: false }));
    }
  };

  const hasRestoredFromRedux = useRef(false);
  const [isReturningFromAgentEdit, setIsReturningFromAgentEdit] = useState(false);
  const loadedTextIdRef = useRef(null);
  const isReturningFromAgentEditRef = useRef(false);

  useEffect(() => {
    isReturningFromAgentEditRef.current = isReturningFromAgentEdit;
  }, [isReturningFromAgentEdit]);

  useEffect(() => {
    document.title = "Typix - Traduction";
  }, []);

  useEffect(() => {
    if (!Array.isArray(clients) || clients.length === 0) {
      dispatch(fetchAdminClientClientList());
    }
  }, [dispatch, clients]);

  const loadTextDetails = useCallback(async (explicitTextId = null) => {
    if (isReturningFromAgentEditRef.current) {
      setIsReturningFromAgentEdit(false);
      isReturningFromAgentEditRef.current = false;
      return;
    }

    const textId =
      (explicitTextId != null && String(explicitTextId).trim()) ||
      (routeTextId ? String(routeTextId) : null) ||
      localStorage.getItem('AdminClient_TextId') ||
      (currentTextId != null ? String(currentTextId) : null);

    try {
      sessionStorage.removeItem(NAVIGATING_TO_TRANSLATION_IA_KEY);
    } catch { }

    if (!textId) return;

    try {
      localStorage.setItem('AdminClient_TextId', String(textId));
      setCurrentTextId(String(textId));
      loadedTextIdRef.current = String(textId);

      const response = await axiosPrivate.get(`/texts/external/unassigned/${textId}/fullDetails`);
      const textData = response.data;

      if (textData) {
        setFormData({
          keyword: textData.keyword || '',
          url: textData.url || '',
          titleTag: textData.titleTag || '',
          metaDescriptionTag: textData.metaDescriptionTag || '',
          agentIA: textData.aiAgentId || '',
          websiteId: textData.websiteId || ''
        });

        setEditorContent(textData.initialHtmlContent || '');
        setTranslatedContent(textData.actualHtmlContent || '');
        if (textData.actualHtmlContent) {
          setCollapseInitialEditorKey((prev) => prev + 1);
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des détails du texte:', error);
      loadedTextIdRef.current = null;
    }
  }, [routeTextId, currentTextId]);

  // Charge fullDetails dès qu'un id est dispo (URL prioritaire)
  useEffect(() => {
    const textId =
      (routeTextId ? String(routeTextId) : null) ||
      localStorage.getItem('AdminClient_TextId');

    if (!textId) return undefined;

    try {
      localStorage.setItem('AdminClient_TextId', String(textId));
    } catch { }
    setCurrentTextId(String(textId));

    let cancelled = false;
    (async () => {
      if (isReturningFromAgentEditRef.current) {
        setIsReturningFromAgentEdit(false);
        isReturningFromAgentEditRef.current = false;
        return;
      }

      try {
        sessionStorage.removeItem(NAVIGATING_TO_TRANSLATION_IA_KEY);
      } catch { }

      try {
        const response = await axiosPrivate.get(
          `/texts/external/unassigned/${textId}/fullDetails`
        );
        if (cancelled) return;

        loadedTextIdRef.current = String(textId);
        const textData = response.data;
        if (!textData) return;

        setFormData({
          keyword: textData.keyword || '',
          url: textData.url || '',
          titleTag: textData.titleTag || '',
          metaDescriptionTag: textData.metaDescriptionTag || '',
          agentIA: textData.aiAgentId || '',
          websiteId: textData.websiteId || '',
        });
        setEditorContent(textData.initialHtmlContent || '');
        setTranslatedContent(textData.actualHtmlContent || '');
        if (textData.actualHtmlContent) {
          setCollapseInitialEditorKey((prev) => prev + 1);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Erreur lors du chargement des détails du texte:', error);
          loadedTextIdRef.current = null;
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [routeTextId]);

  useEffect(() => {
    fetchTranslationAgents();
  }, []);

  // Pré-remplir les champs depuis la popin latérale (Mot clé / URL / Agent IA)
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(TRANSLATION_POPIN_CONTEXT_KEY);
      if (!raw) return;
      const ctx = JSON.parse(raw);
      if (!ctx || !ctx.form) return;
      const { form } = ctx;

      setFormData((prev) => ({
        ...prev,
        keyword: form.keyword || prev.keyword,
        url: form.targetUrl || form.url || prev.url,
        agentIA: form.agentId || prev.agentIA,
      }));

      window.localStorage.removeItem(TRANSLATION_POPIN_CONTEXT_KEY);
    } catch (e) {
      // ignore JSON / storage errors
    }
  }, []);

  useEffect(() => {
    const fetchSelectedWebsiteId = () => {
      try {
        const selectedWebsiteUrl = localStorage.getItem('AC_Dashboard_SelectedWebsite') || sessionStorage.getItem('AC_Dashboard_SelectedWebsite');
        if (!selectedWebsiteUrl) {
          setSelectedWebsiteId(null);
          return;
        }

        const rawList = Array.isArray(clients) ? clients : clients?.websites || clients?.data || [];

        const matchedWebsite = rawList.find((w) =>
          (w.urlSite || w.url || w.name) === selectedWebsiteUrl
        );

        if (matchedWebsite?.id) {
          setSelectedWebsiteId(Number(matchedWebsite.id));
        } else {
          setSelectedWebsiteId(null);
        }
      } catch (error) {
        console.warn('Erreur lors de la récupération du websiteId:', error);
        setSelectedWebsiteId(null);
      }
    };

    if (Array.isArray(clients) && clients.length > 0) {
      fetchSelectedWebsiteId();
    }
  }, [clients]);

  useEffect(() => {
    if (formData?.websiteId) {
      fetchTranslationAgents();
    }
  }, [formData?.websiteId]);

  useEffect(() => {
    let isMounted = true;

    const loadCompanyQuotas = async () => {
      try {
        if (!isMounted) return;
        await refreshCompanyQuotas();
      } catch {
        if (isMounted) {
          setRemainingCredits(null);
        }
      }
    };

    loadCompanyQuotas();
    return () => {
      isMounted = false;
    };
  }, [refreshCompanyQuotas]);

  const fetchTranslationAgents = async () => {
    try {
      setLoadingAgents(true);

      let selectedWebsiteUrl = null;
      try {
        const candidateKeys = [
          'AC_Dashboard_SelectedWebsite',
          'AdminClient_SelectedWebsite',
          'selectedWebsite',
        ];
        let stored = null;
        for (const key of candidateKeys) {
          const val = localStorage.getItem(key) || sessionStorage.getItem(key);
          if (val) {
            stored = val;
            console.log('[Traduction IA] Found selectedWebsite in key:', key);
            break;
          }
        }
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            const candidateUrl =
              parsed?.urlSite ||
              parsed?.url ||
              parsed?.website?.urlSite ||
              parsed?.selectedWebsite?.urlSite ||
              parsed?.selectedWebsite?.url;
            if (candidateUrl) {
              selectedWebsiteUrl = String(candidateUrl);
            } else if (typeof parsed === 'string') {
              selectedWebsiteUrl = parsed;
            }
          } catch (_) {
            // stored n'est pas du JSON, on le considère comme une URL brute
            selectedWebsiteUrl = String(stored).replace(/"/g, '').trim();
          }
        }
      } catch (_) { }

      if (!selectedWebsiteUrl) {
        setTranslationAgents([]);
        return;
      }

      const response = await axiosPrivate.get(
        `/smart-content/agent_ia/traduction/all?websiteUrl=${encodeURIComponent(
          selectedWebsiteUrl
        )}`
      );
      const agents = (response.data || []).map((agent) => ({
        ...agent,
        displayName: agent.name,
        websiteUrl: selectedWebsiteUrl,
      }));
      setTranslationAgents(agents);
    } catch (error) {
      console.error('Erreur lors du chargement des agents de traduction:', error);
      setTranslationAgents([]);
    } finally {
      setLoadingAgents(false);
    }
  };

  const navigateToAgentEdit = (agentId) => {
    const textIdFromStorage = resolveActualTextId(currentTextId);
    if (!textIdFromStorage) {
      dispatch(saveTranslationSnapshot({
        formData,
        editorContent,
        translatedContent,
        currentTextId,
        translationAgents,
      }));
    }
    sessionStorage.setItem('isNavigatingToAgentEdit', 'true');
    localStorage.setItem('agentId', String(agentId));
    localStorage.setItem('agentMissionType', 'Traduction');
    history.push('/AdminClient/createagentiafromlist');
  };

  useEffect(() => {
    if (hasRestoredFromRedux.current) return;
    const textIdFromStorage = resolveActualTextId(currentTextId);
    const isNavigatingToAgentEdit = sessionStorage.getItem('isNavigatingToAgentEdit') === 'true';
    if (textIdFromStorage || currentTextId) {
      if (isNavigatingToAgentEdit) {
        try { loadTextDetails(); } catch { }
      }
      try { sessionStorage.removeItem('isNavigatingToAgentEdit'); } catch { }
      return;
    }
    if (!isNavigatingToAgentEdit) {
      return;
    }
    const restoreTimeout = setTimeout(() => {
      if (reduxTranslationState && reduxTranslationState.timestamp) {
        const maxAge = 30 * 60 * 1000;
        const age = Date.now() - reduxTranslationState.timestamp;
        if (age < maxAge && age >= 0) {
          hasRestoredFromRedux.current = true;
          if (reduxTranslationState.formData) setFormData(reduxTranslationState.formData);
          if (reduxTranslationState.editorContent) setEditorContent(reduxTranslationState.editorContent);
          if (reduxTranslationState.translatedContent) setTranslatedContent(reduxTranslationState.translatedContent);
          if (reduxTranslationState.currentTextId) setCurrentTextId(reduxTranslationState.currentTextId);
          if (reduxTranslationState.translationAgents) setTranslationAgents(reduxTranslationState.translationAgents);
          setIsReturningFromAgentEdit(true);
        }
      }
      try { sessionStorage.removeItem('isNavigatingToAgentEdit'); } catch { }
    }, 100);
    return () => clearTimeout(restoreTimeout);
  }, [reduxTranslationState, currentTextId]);

  const handleCreateCustomAgent = () => {
    localStorage.setItem('agentMissionType', 'Traduction');
    localStorage.removeItem('agentId');
    history.push('/AdminClient/createagentiafromlist');
  };

  const getTextFromHtml = (html) => {
    const div = document.createElement('div');
    div.innerHTML = html || '';
    return div.textContent || div.innerText || '';
  };

  const buildSavePayload = () => {

    const websiteId = (formData.websiteId && Number(formData.websiteId) !== 0)
      ? Number(formData.websiteId)
      : (selectedWebsiteId || 0);

    const payload = {
      keyword: formData.keyword || '',
      url: formData.url || '',
      titleTag: formData.titleTag || '',
      metaDescriptionTag: formData.metaDescriptionTag || '',
      actualTextContent: getTextFromHtml(translatedContent),
      initialTextContent: getTextFromHtml(editorContent),
      actualHtmlContent: translatedContent,
      initialHtmlContent: editorContent,
      activeInitialText: true,
      type: 'TRANSLATED',
      numberWords: getTextFromHtml(translatedContent).split(/\s+/).filter(word => word.length > 0).length,
      maxWords: 0,
      internalLinking: '',
      aiAgentId: formData.agentIA || '',
      aiAgentModel: 'GPT_4O',
      websiteId: websiteId
    };
    console.log('Payload de sauvegarde:', payload); // Debug
    return payload;
  };

  const buildUpdatePayload = () => {
    const websiteId = (formData.websiteId && Number(formData.websiteId) !== 0)
      ? Number(formData.websiteId)
      : (selectedWebsiteId || 0);

    return {
      keyword: formData.keyword || '',
      url: formData.url || '',
      titleTag: formData.titleTag || '',
      metaDescriptionTag: formData.metaDescriptionTag || '',
      actualTextContent: getTextFromHtml(translatedContent),
      initialTextContent: getTextFromHtml(editorContent),
      actualHtmlContent: translatedContent,
      initialHtmlContent: editorContent,
      activeInitialText: true,
      numberWords: getTextFromHtml(translatedContent).split(/\s+/).filter(word => word.length > 0).length,
      type: 'TRANSLATED',
      internalLinking: '',
      aiAgentId: formData.agentIA || '',
      websiteId: websiteId
    };
  };

  const buildDeliverPayload = () => {
    return {
      keyword: formData.keyword || '',
      url: formData.url || '',
      titleTag: formData.titleTag || '',
      metaDescriptionTag: formData.metaDescriptionTag || '',
      actualTextContent: getTextFromHtml(translatedContent),
      actualHtmlContent: removeStrongTags(translatedContent),
      numberWords: getTextFromHtml(translatedContent).split(/\s+/).filter(word => word.length > 0).length,
      internalLinking: ''
    };
  };

  const buildValidatePayload = () => {
    return {
      actualTextContent: getTextFromHtml(translatedContent),
      actualHtmlContent: removeStrongTags(translatedContent)
    };
  };

  const handleSave = async () => {
    if (!translatedContent || translatedContent.trim() === '') {
      alert('Veuillez d\'abord traduire un texte avant de sauvegarder.');
      return;
    }

    if (!editorContent || editorContent.trim() === '') {
      alert('Le texte source est requis pour la sauvegarde.');
      return;
    }

    setSaveError('');
    setSaveSuccess(false);
    setIsSaving(true);

    try {
      const payload = buildSavePayload();
      const response = await axiosPrivate.post('/texts/external/unassigned', payload);

      if (response.data?.id) {
        syncTextIdStorageAndUrl(response.data.id);
      }

      setSaveSuccess(true);
      setIsSuccess(true);
      setErrorSuccessMsg('Texte traduit enregistré avec succès');
      setErrorSuccess(true);

      try {
        await refreshCompanyQuotas();
      } catch (quotaError) {
        console.warn('Erreur lors du rafraîchissement des crédits:', quotaError);
      }

      dispatch(clearTranslationSnapshot());
      hasRestoredFromRedux.current = false;
      setTimeout(() => {
        setSaveSuccess(false);
        setErrorSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement du texte traduit:', error);
      const errorMessage = error?.response?.data?.message || error.message || 'Erreur lors de l\'enregistrement';
      setSaveError(errorMessage);
      alert(`Erreur lors de l'enregistrement: ${errorMessage}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!currentTextId) return;
    setSaveError('');
    setSaveSuccess(false);
    setIsSaving(true);
    try {
      const payload = buildUpdatePayload();
      await axiosPrivate.put(`/texts/external/unassigned/${currentTextId}`, payload);
      setSaveSuccess(true);
      setIsSuccess(true);
      setErrorSuccessMsg('Texte mis à jour avec succès');
      setErrorSuccess(true);

      try {
        await refreshCompanyQuotas();
      } catch (quotaError) {
        console.warn('Erreur lors du rafraîchissement des crédits:', quotaError);
      }

      dispatch(clearTranslationSnapshot());
      hasRestoredFromRedux.current = false;
      setTimeout(() => {
        setSaveSuccess(false);
        setErrorSuccess(false);
      }, 3000);
    } catch (error) {
      console.error(t('Erreur lors de la mise à jour du texte:'), error);
      setSaveError(error?.response?.data?.message || error.message || t('Erreur lors de la mise à jour'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeliver = async () => {
    if (!currentTextId) return;
    setDeliverError('');
    setDeliverSuccess(false);
    setIsDelivering(true);
    try {
      const payload = buildDeliverPayload();
      await axiosPrivate.put(`/texts/external/unassigned/${currentTextId}/deliver`, payload);
      await loadTextDetails();
      setDeliverSuccess(true);
      setIsSuccess(true);
      setErrorSuccessMsg(t('Texte livré avec succès'));
      setErrorSuccess(true);
      dispatch(clearTranslationSnapshot());
      hasRestoredFromRedux.current = false;
      setTimeout(() => {
        setDeliverSuccess(false);
        setErrorSuccess(false);
      }, 3000);
    } catch (error) {
      console.error(t('Erreur lors de la livraison du texte:'), error);
      setDeliverError(error?.response?.data?.message || error.message || t('Erreur lors de la livraison'));
    } finally {
      setIsDelivering(false);
    }
  };

  const handleValidate = async () => {
    if (!currentTextId) return;
    setValidateError('');
    setValidateSuccess(false);
    setIsValidating(true);
    try {
      const payload = buildValidatePayload();
      await axiosPrivate.put(`/texts/external/unassigned/${currentTextId}/validate`, payload);
      await loadTextDetails();
      setValidateSuccess(true);
      setIsSuccess(true);
      setErrorSuccessMsg(t('Texte validé avec succès'));
      setErrorSuccess(true);
      dispatch(clearTranslationSnapshot());
      hasRestoredFromRedux.current = false;
      setTimeout(() => {
        setValidateSuccess(false);
        setErrorSuccess(false);
      }, 3000);
    } catch (error) {
      console.error(t('Erreur lors de la validation du texte:'), error);
      setValidateError(error?.response?.data?.message || error.message || t('Erreur lors de la validation'));
    } finally {
      setIsValidating(false);
    }
  };

  const handleGoOnline = async () => {
    if (!currentTextId || isGoingOnline) return;

    setActionsOpen(false);
    setDeployError('');
    setDeploySuccess(false);
    setIsGoingOnline(true);
    try {
      const payload = {
        textId: String(currentTextId),
        deployed: true,
        url: formData.url || '',
      };
      await axiosPrivate.post('/texts/external/unassigned/deploy-new-url', payload);
      setDeploySuccess(true);
      setIsSuccess(true);
      setErrorSuccessMsg(t('Texte mis en ligne avec succès'));
      setErrorSuccess(true);
      dispatch(clearTranslationSnapshot());
      hasRestoredFromRedux.current = false;
      setTimeout(() => {
        setDeploySuccess(false);
        setErrorSuccess(false);
      }, 3000);
    } catch (error) {
      console.error(t('Erreur lors de la mise en ligne du texte:'), error);
      setDeployError(
        error?.response?.data?.message || error.message || t('Erreur lors de la mise en ligne')
      );
    } finally {
      setIsGoingOnline(false);
    }
  };

  const handleWordPressDeploy = async () => {
    if (!currentTextId || isDeploying) return;

    setActionsOpen(false);
    setDeployError('');
    setDeploySuccess(false);
    setWordPressDeployContent(translatedContent || '');
    setShowWordPressDeployModal(true);
  };

  const handleWordPressDeploySuccess = async () => {
    if (!currentTextId) return;

    setIsDeploying(true);
    try {
      setDeploySuccess(true);
      setIsSuccess(true);
      setErrorSuccessMsg(t('Texte déployé avec succès sur WordPress'));
      setErrorSuccess(true);
      dispatch(clearTranslationSnapshot());
      hasRestoredFromRedux.current = false;
      setTimeout(() => {
        setDeploySuccess(false);
        setErrorSuccess(false);
      }, 3000);
    } catch (error) {
      console.error(t('Erreur après déploiement WordPress:'), error);
      setDeployError(
        error?.response?.data?.message || error.message || t('Erreur lors du déploiement')
      );
    } finally {
      setIsDeploying(false);
    }
  };

  const handleSendToRevision = async () => {
    if (!currentTextId || isSendingToRevision) return;
    setActionsOpen(false);
    setRevisionError('');
    setIsSendingToRevision(true);
    try {
      await axiosPrivate.put(
        `/texts/external/${currentTextId}/update-status?textStatusType=${encodeURIComponent('IN_REVISION')}`
      );
      setIsSuccess(true);
      setErrorSuccessMsg(t('Le texte a été mis en révision avec succès.'));
      setErrorSuccess(true);
      dispatch(clearTranslationSnapshot());
      hasRestoredFromRedux.current = false;
      setTimeout(() => {
        setErrorSuccess(false);
      }, 3000);
    } catch (error) {
      if (isTextRevisionNotAllowedError(error)) {
        setIsRevisionErrorModalOpen(true);
      } else {
        console.error('Erreur lors de la mise en révision:', error);
        setRevisionError(
          error?.response?.data?.message || error.message || t('Erreur lors de la mise en révision')
        );
      }
    } finally {
      setIsSendingToRevision(false);
    }
  };

  const handleGoBack = () => {
    const isNavigatingToAgentEdit = sessionStorage.getItem('isNavigatingToAgentEdit') === 'true';
    if (!isNavigatingToAgentEdit) {
      dispatch(clearTranslationSnapshot());
      hasRestoredFromRedux.current = false;
    }
    try { sessionStorage.removeItem('isNavigatingToAgentEdit'); } catch { }
    if (!isNavigatingToAgentEdit) {
      try { localStorage.removeItem('AdminClient_TextId'); } catch { }
    }

    let cameFromLogin = Boolean(location.state?.fromLogin);
    try {
      cameFromLogin = cameFromLogin || sessionStorage.getItem('cameFromLogin') === '1';
      if (cameFromLogin) sessionStorage.removeItem('cameFromLogin');
    } catch { }

    if (cameFromLogin) {
      history.replace(
        isConsultantRoute ? '/Consultant/MainMenu' : '/AdminClient/AdminMainMenu'
      );
      return;
    }

    history.push(translationIaListPath);
  };

  const handleTranslate = async () => {
    if (remainingCredits !== null && remainingCredits <= 0) {
      setIsLimitModalOpen(true);
      return;
    }

    const newErrors = {
      keywordError: !formData.keyword || formData.keyword.trim() === '',
      agentError: !formData.agentIA,
      editorError: !editorContent || editorContent.trim() === ''
    };
    setErrors(prev => ({ ...prev, ...newErrors }));
    if (newErrors.keywordError || newErrors.agentError || newErrors.editorError) {
      return;
    }

    try {
      setIsTranslating(true);
      setCollapseInitialEditorKey((prev) => prev + 1);

      try {
        await refreshCompanyQuotas();
      } catch (quotaError) {
        console.warn('Erreur lors du rafraîchissement des crédits:', quotaError);
      }

      setTranslatedContent(`
        <div style="text-align: center; padding: 40px;">
          <img src="/Images/gifencours.gif" alt={t("Traduction en cours")} style="width: 200px; height: 200px; margin-bottom: -30px;" />
          <p style="color: #666; font-size: 16px; font-weight: 500;">Traduction en cours...</p>
        </div>
      `);

      const response = await axiosPrivate.post(
        '/smart-content/smart_content/traduction/generate_text?maxTokens=10000&chatGPTModel=GPT_4_1&temperature=0.3',
        {
          agentId: formData.agentIA,
          keyword: formData.keyword || "",
          urlPage: formData.url || "",
          originalText: editorContent
        }
      );

      if (response.data) {
        const translated =
          response.data.output ??
          response.data.translatedText ??
          response.data;
        setTranslatedContent(translated);
      }

      try {
        await refreshCompanyQuotas();
      } catch (quotaError) {
        console.warn('Erreur lors du rafraîchissement des crédits:', quotaError);
      }
    } catch (error) {
      console.error(t('Erreur lors de la traduction:'), error);
      console.error(t('Détails de l\'erreur:'), error.response?.data);
      console.error(t('Status:'), error.response?.status);
      setTranslatedContent(`<p><em>${GENERATION_ERROR_MESSAGE}</em></p>`);
    } finally {
      setIsTranslating(false);
      try {
        await refreshCreditsWithDelay(600);
      } catch (quotaError) {
        console.warn('Erreur lors du rafraîchissement des crédits:', quotaError);
      }
    }
  };
  const isCreditsExhausted = remainingCredits !== null && remainingCredits <= 0;

  return (
    <div className="content-center">
      <style>{`
        .content-center .box-wrapper-gray input[type="text"]:not(.MuiInputBase-input) {
          background-color: white !important;
          color: black !important;
          border: 1px solid #ced4da !important;
        }
        .content-center .box-wrapper-gray select:not(.MuiAutocomplete-root) {
          background-color: white !important;
          color: black !important;
          border: 1px solid #ced4da !important;
        }
        

        .agent-ia-dropdown {
          width: 100% !important;
          border: 1px solid #cfcfcf !important;
          background: #ffffff !important;
          border-radius: 8px !important;
          padding: 12px 16px !important;
          font-size: 16px !important;
          color: #3c3c3c !important;
          -webkit-appearance: none !important;
          -moz-appearance: none !important;
          appearance: none !important;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23666' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") !important;
          background-repeat: no-repeat !important;
          background-position: right 12px center !important;
          background-size: 12px 8px !important;
          padding-right: 36px !important;
        }
        
        .agent-ia-dropdown:focus {
          outline: none !important;
          border-color: #0064d2 !important;
          box-shadow: 0 0 0 3px rgba(0, 100, 210, 0.1) !important;
        }
        
        .agent-ia-dropdown::placeholder {
          color: #414040 !important;
          font-style: normal !important;
          font-weight: 400 !important;
        }
        
        /* STYLES CUSTOMAUTOCOMPLETE - FOND BLANC FORCÉ */
        .content-center .box-wrapper-gray .MuiAutocomplete-root {
          background-color: #fff !important;
        }
        
        .content-center .box-wrapper-gray .MuiAutocomplete-root .MuiOutlinedInput-root {
          background-color: #fff !important;
        }
        
        .content-center .box-wrapper-gray .MuiAutocomplete-root .MuiOutlinedInput-root .MuiOutlinedInput-input {
          background-color: transparent !important;
        }
        
        .MuiAutocomplete-paper {
          border-radius: 8px !important;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
          margin-top: 4px !important;
          overflow-x: hidden !important;
          max-height: 200px !important;
        }
        
        .MuiAutocomplete-listbox {
          overflow-x: hidden !important;
          overflow-y: auto !important;
          padding: 8px 0 !important;
          max-height: 200px !important;
        }
        
        .MuiAutocomplete-option {
          border-radius: 4px !important;
          margin: 2px 8px !important;
          padding: 8px 12px !important;
          transition: background-color 0.2s ease !important;
        }
        
        .MuiAutocomplete-option:hover {
          background-color: #ffe42666 !important;
        }
        
        .MuiAutocomplete-option[aria-selected="true"] {
          background-color: #ffe426 !important;
        }
        
        .MuiAutocomplete-listbox {
          padding: 8px 0 !important;
          border-radius: 8px !important;
        }
      `}</style>
      <div className="box-info-header">
        <div className="b-child-1">
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <img
              src="/Images/icon-back.png"
              alt="Retour"
              width={50}
              height={50}
              style={{
                cursor: 'pointer',
                padding: '8px',
                borderRadius: '50%',
                backgroundColor: '#f5f5f5',
                transition: 'background-color 0.2s ease'
              }}
              onClick={handleGoBack}



            />
            <h1>{t('Traduction IA')}</h1>
          </div>
        </div>
      </div>

      <DetailsIATraduction
        t={t}
        expanded={expanded}
        setExpanded={setExpanded}
        formData={formData}
        errors={errors}
        isFieldDisabled={isFieldDisabled}
        handleChange={handleChange}
        handleBlur={handleBlur}
        translationAgents={translationAgents}
        loadingAgents={loadingAgents}
        history={history}
        onCreateCustomAgent={isConsultantRoute ? undefined : handleCreateCustomAgent}
        onAgentChange={handleAgentChange}
        onNavigateToAgentEdit={isConsultantRoute ? undefined : navigateToAgentEdit}
        onStartTranslation={isConsultantRoute ? undefined : handleTranslate}
        isTranslating={isTranslating}
        hasTranslatedContent={!!translatedContent}
        isCreditsExhausted={isCreditsExhausted}
        onCreditsLimitClick={() => setIsLimitModalOpen(true)}
        onOpenTranslationSettings={() => { }}
        remainingCredits={isConsultantRoute ? null : remainingCredits}
      />


      <div className="row mt-2">
        <InitialTextSection
          userRole={ROLES.ADMIN_CLIENT}
          textForm={{ initialHtmlContent: editorContent }}
          textType={'TRANSLATION'}
          url={formData.url}
          initialHtmlContent={editorContent}
          initialTextContent={initialTextContent}
          isTextFieldDisabled={isConsultantRoute}
          formData={{ activeInitialText: true }}
          handleStepOneEditorInit={() => { }}
          setInitialHtmlContent={setEditorContent}
          setInitialTextContent={setInitialTextContent}
          setInitialTextWordCount={setInitialTextWordCount}
          t={t}
          isNotTranslation={false}
          isSaveDisabled={isConsultantRoute}
          isTextSaved={false}
          externalCollapseKey={collapseInitialEditorKey}
          textId={currentTextId}
          onDownloadWord={handleDownloadWord}
        />
      </div>
      {(translatedContent || isTranslating) && (
        <div className="row mt-4">
          <div className="col-12">

            <div
              className="box-wrapper-editor"
              style={{
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                borderRadius: '8px',
                overflow: 'hidden'
              }}
            >
              <CustomEditor
                content={translatedContent}
                textId={currentTextId}
                onDownloadWord={handleDownloadWord}
                downloadFileName={formData.keyword || 'document'}
                onChange={(content) => setTranslatedContent(content)}
                t={t}
                height="800px"
                disabled={isTranslating || isConsultantRoute}
                placeholder={isTranslating ? "Traduction en cours..." : "Le texte traduit apparaîtra ici..."}
              />
            </div>
            {!isConsultantRoute && (
              <div className="d-flex justify-content-end mt-3">
                {!currentTextId ? (
                  <button
                    className="bt-submit-y"
                    onClick={handleSave}
                    disabled={isSaving || !translatedContent || translatedContent.trim() === '' || !editorContent || editorContent.trim() === ''}
                    style={{
                      backgroundColor: '#6458F1', color: 'white',
                      opacity: isSaving || !translatedContent || translatedContent.trim() === '' || !editorContent || editorContent.trim() === '' ? 0.6 : 1,
                      cursor: isSaving || !translatedContent || translatedContent.trim() === '' || !editorContent || editorContent.trim() === '' ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {isSaving ? t('Enregistrement...') : saveSuccess ? t('Enregistré') : t('Sauvegarder')}
                  </button>
                ) : (
                  // Dropdown Actions quand il y a un ID
                  <div style={{ position: 'relative' }}>
                    <button
                      type="button"
                      className="btn"
                      onClick={() => setActionsOpen(!actionsOpen)}
                      style={{
                        background: '#6458F1',
                        border: 'none',
                        color: '#fff',
                        borderRadius: '9999px',
                        padding: '12px 50px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 14,
                        boxShadow: '0 0 12px 1px rgba(0,0,0,0.05)',
                        textTransform: 'none'
                      }}
                    >
                      {t('Actions')}
                      <span style={{ opacity: 0.6 }}>|</span>
                      <i className={`fa fa-chevron-${actionsOpen ? 'up' : 'down'}`} />
                    </button>
                    {actionsOpen && (
                      <div
                        style={{
                          position: 'absolute',
                          right: 0,
                          bottom: '100%',
                          marginBottom: 8,
                          background: '#fff',
                          borderRadius: 12,
                          boxShadow:
                            '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)'
                        }}
                      >
                        <div
                          onClick={isSaving ? undefined : handleUpdate}
                          style={{
                            padding: '12px 16px',
                            borderBottom: '1px solid #f0f0f0',
                            borderTopLeftRadius: 12,
                            borderTopRightRadius: 12,
                            background: isSaving ? '#f5f5f5' : '#8B82F7',
                            color: isSaving ? '#666' : '#fff',
                            cursor: isSaving ? 'not-allowed' : 'pointer',
                            opacity: isSaving ? 0.7 : 1
                          }}
                        >
                          {isSaving ? 'Enregistrement...' : saveSuccess ? 'Enregistré' : 'Sauvegarder'}
                        </div>
                        <div
                          onClick={isDelivering ? undefined : handleDeliver}
                          style={{
                            padding: '12px 16px',
                            borderTop: '1px solid #f0f0f0',
                            background: isDelivering ? '#f5f5f5' : '#fff',
                            cursor: isDelivering ? 'not-allowed' : 'pointer',
                            opacity: isDelivering ? 0.7 : 1
                          }}
                        >
                          {isDelivering ? 'Livraison...' : deliverSuccess ? 'Livré' : 'Livrer'}
                        </div>
                        <div
                          onClick={isSendingToRevision ? undefined : handleSendToRevision}
                          style={{
                            padding: '12px 16px',
                            borderTop: '1px solid #f0f0f0',
                            background: isSendingToRevision ? '#f5f5f5' : '#fff',
                            cursor: isSendingToRevision ? 'not-allowed' : 'pointer',
                            opacity: isSendingToRevision ? 0.7 : 1
                          }}
                        >
                          {isSendingToRevision ? t('Mise en révision...') : t('En révision')}
                        </div>
                        <div
                          onClick={isValidating ? undefined : handleValidate}
                          style={{
                            padding: '12px 16px',
                            borderTop: '1px solid #f0f0f0',
                            background: isValidating ? '#f5f5f5' : '#fff',
                            cursor: isValidating ? 'not-allowed' : 'pointer',
                            opacity: isValidating ? 0.7 : 1
                          }}
                        >
                          {isValidating ? 'Validation...' : validateSuccess ? 'Validé' : 'Valider'}
                        </div>
                        <div
                          onClick={isGoingOnline ? undefined : handleGoOnline}
                          style={{
                            padding: '12px 16px',
                            borderTop: '1px solid #f0f0f0',
                            background: isGoingOnline ? '#f5f5f5' : '#fff',
                            cursor: isGoingOnline ? 'not-allowed' : 'pointer',
                            opacity: isGoingOnline ? 0.7 : 1
                          }}
                        >
                          {isGoingOnline ? t('En ligne...') : deploySuccess ? t('En ligne') : t('En ligne')}
                        </div>
                        <div
                          onClick={isDeploying ? undefined : handleWordPressDeploy}
                          style={{
                            padding: '12px 16px',
                            borderBottomLeftRadius: 12,
                            borderBottomRightRadius: 12,
                            borderTop: '1px solid #f0f0f0',
                            background: isDeploying ? '#f5f5f5' : '#fff',
                            cursor: isDeploying ? 'not-allowed' : 'pointer',
                            opacity: isDeploying ? 0.7 : 1
                          }}
                        >
                          {isDeploying ? t('Déploiement...') : t('Déployer via WordPress')}
                        </div>
                        {saveError && (
                          <div style={{ padding: '8px 16px', color: '#c33' }}>
                            {saveError}
                          </div>
                        )}
                        {deliverError && (
                          <div style={{ padding: '8px 16px', color: '#c33' }}>
                            {deliverError}
                          </div>
                        )}
                        {revisionError && (
                          <div style={{ padding: '8px 16px', color: '#c33' }}>
                            {revisionError}
                          </div>
                        )}
                        {validateError && (
                          <div style={{ padding: '8px 16px', color: '#c33' }}>
                            {validateError}
                          </div>
                        )}
                        {deployError && (
                          <div style={{ padding: '8px 16px', color: '#c33' }}>
                            {deployError}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <QuitProjectCreationModal
        isOpen={showSaveBeforeDownloadModal}
        onClose={() => setShowSaveBeforeDownloadModal(false)}
        title={t('Téléchargement')}
        message={t('Veuillez enregistrer le texte avant de pouvoir le télécharger.')}
        showSuggestion={false}
        showSecondaryButton={false}
        primaryButtonLabel={t('Fermer')}
        onPrimaryAction={() => setShowSaveBeforeDownloadModal(false)}
      />

      <QuitProjectCreationModal
        isOpen={isRevisionErrorModalOpen}
        onClose={() => setIsRevisionErrorModalOpen(false)}
        variant="enrichment"
        title={t('Mise en révision impossible')}
        message={t(
          'Ce texte est validé ou mis en ligne. Il ne peut pas être envoyé en révision.'
        )}
        primaryButtonLabel={t('Fermer')}
        onPrimaryAction={() => setIsRevisionErrorModalOpen(false)}
        showSecondaryButton={false}
        showSuggestion={false}
      />

      <ErrorSuccesModal
        open={errorSuccess}
        message={errorSuccessMsg}
        handleClose={() => setErrorSuccess(false)}
        success={isSuccess}
        toggle={() => setErrorSuccess(!errorSuccess)}
      />
      <ProjectCreatedModal
        isOpen={isLimitModalOpen}
        onClose={() => setIsLimitModalOpen(false)}
        onConfirm={() => {
          setIsLimitModalOpen(false);
          setIsBuyCreditsModalOpen(true);
        }}
        title={t('Limite atteinte')}
        message={t(
          'Vous avez atteint la limite autorisée par votre forfait.\n\nRechargez votre compte dès maintenant pour continuer à générer du contenu, enrichir vos textes et utiliser vos agents sans interruption.'
        )}
        confirmButtonText={t('Acheter des crédits')}
        secondaryButtonText={t('Passer à une offre supérieure')}
        onSecondaryAction={() => history.push('/AdminClient/Forfait')}
      />
      <BuyCreditsModal
        open={isBuyCreditsModalOpen}
        onClose={() => setIsBuyCreditsModalOpen(false)}
      />
      <WordPressDeployModal
        isOpen={showWordPressDeployModal}
        onClose={() => setShowWordPressDeployModal(false)}
        textId={currentTextId}
        websiteId={formData.websiteId || selectedWebsiteId}
        initialKeyword={formData.keyword || ''}
        initialContent={wordPressDeployContent}
        onDeploySuccess={handleWordPressDeploySuccess}
      />
    </div>
  );
};

export default CreateTraductionIA;
